import React, { FC, useCallback, useMemo } from "react"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { TableHeader } from "../../../../../Common/Table/TableHeader"
import { Loader } from "dash-styled"
import { useCustomers } from "../../../../../../Hooks/useCustomers"
import {
    BackofficeCustomerParams,
    BackofficeCustomerQueryModel,
    Customer
} from "../../../../../../Models/API/BackofficeModel"
import { Table } from "resha"
import { customerColumns } from "./CustomerColumns"
import { SearchCustomers } from "./SearchCustomers"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import { BackofficeDashboardBase } from "../../DashboardBase/BackofficeDashboardBase"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import {
    ConstFilterOperator,
    FilterExpression
} from "../../../../../../Models/Client/FilterExpression"
import {
    SortChooser,
    SortChooserProps
} from "../../../../../Common/Button/SortChooser"
import {
    FilterChooser,
    FilterChooserProps
} from "../../../../../Common/Button/FilterChooser"
import {
    backofficeCustomerSortFields,
    backofficeCustomerFilterFields,
    defaultBackofficeCustomerFilter
} from "./constants"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { loadMoreCustomerList } from "../../../../../../Actions/API/Backoffice/BackofficeActions"

interface CustomerListProps {
    filterAndSort: FilterAndSortExpression<BackofficeCustomerQueryModel>
    params: BackofficeCustomerParams
}

const removeSearchEmailFilter = (
    f: FilterExpression<BackofficeCustomerQueryModel>
) => f.fieldName !== "searchEmail"

export const CustomerList: FC<CustomerListProps> = ({
    filterAndSort,
    params
}) => {
    const dispatch = useDispatch()

    const customersFetch = useCustomers(params)
    const handleSearch = useCallback(
        (searchEmail: string) => {
            const updatedFilter = filterAndSort.filter.filter(
                removeSearchEmailFilter
            )
            dispatch(
                push(
                    routePaths.customers({
                        ...filterAndSort,
                        filter: [
                            {
                                fieldName: "searchEmail",
                                value: searchEmail ?? "",
                                operator: ConstFilterOperator.eq
                            },
                            ...updatedFilter
                        ]
                    })
                )
            )
        },
        [dispatch, filterAndSort]
    )
    const handleRowClick = useCallback(
        (user: Customer) =>
            !user.deleted &&
            dispatch(push(routePaths.customerDetails(user.username))),
        [dispatch]
    )

    const extractJobRowClassName = useCallback(
        (user: Customer) => (user.deleted ? "bg-smoke-400 cursor-default" : ""),
        []
    )

    const handleChangeSort = useCallback(
        (s: SortExpression<BackofficeCustomerQueryModel>) => {
            dispatch(push(routePaths.customers({ ...filterAndSort, sort: s })))
        },
        [filterAndSort, dispatch]
    )

    const handleChangeFilters = useCallback(
        (f: FilterExpression<BackofficeCustomerQueryModel>[]) => {
            const searchFilter = filterAndSort.filter.find(
                f => f.fieldName === "searchEmail"
            )
            dispatch(
                push(
                    routePaths.customers({
                        ...filterAndSort,
                        filter: searchFilter ? [searchFilter, ...f] : f
                    })
                )
            )
        },
        [filterAndSort, dispatch]
    )

    const sortOptions = useMemo<SortChooserProps<BackofficeCustomerQueryModel>>(
        () => ({
            fields: backofficeCustomerSortFields,
            applied: filterAndSort.sort,
            onChange: handleChangeSort
        }),
        [filterAndSort, handleChangeSort]
    )
    const filterOptions = useMemo<
        FilterChooserProps<BackofficeCustomerQueryModel>
    >(
        () => ({
            fields: backofficeCustomerFilterFields,
            applied: filterAndSort.filter.filter(removeSearchEmailFilter),
            onChange: handleChangeFilters,
            defaultOnAdd: defaultBackofficeCustomerFilter
        }),
        [filterAndSort, handleChangeFilters]
    )

    const handleLoadMore = useMemo(
        () =>
            getValue(customersFetch)?.canLoadMore
                ? async () => dispatch(loadMoreCustomerList())
                : undefined,
        [dispatch, customersFetch]
    )

    const renderContent = () => {
        if (!hasValue(customersFetch)) {
            return <Loader testId="backoffice-loader" />
        }

        return (
            <div className="flex-grow h-full flex flex-col">
                <TableHeader className="mr-6" />
                <div className="flex-grow h-0 flex-basis-0 overflow-y-auto pr-6">
                    <Table<Customer>
                        className="w-full"
                        columns={customerColumns}
                        getKey={user => user.username}
                        items={customersFetch.value.customers}
                        stickyHeader={true}
                        onRowClick={handleRowClick}
                        itemCount={customersFetch.value.total}
                        loadMore={handleLoadMore}
                        extractJobRowClassName={extractJobRowClassName}
                    />
                </div>
            </div>
        )
    }

    return (
        <BackofficeDashboardBase
            title="Backoffice"
            titleComponent="h1"
            breadcrumbs={["Sales backoffice", "Customer search"]}
            fixedContentWidth={false}
        >
            <div className="flex z-20">
                <SearchCustomers
                    searchEmail={params.searchEmail}
                    onSearch={handleSearch}
                />
                <div className="ml-2 flex">
                    <SortChooser
                        {...sortOptions}
                        testId="table-sort-chooser"
                        classNameOpen="text-smoke-610 shadow-none"
                        classNameClosed="text-smoke-610 shadow-none"
                    />
                </div>
                <div className="ml-2 flex">
                    <FilterChooser
                        {...filterOptions}
                        testId="table-filter-chooser"
                    />
                </div>
            </div>
            {renderContent()}
        </BackofficeDashboardBase>
    )
}
